import * as QueryString from 'query-string';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { cssClass } from '~src/style';

import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '../../app/state/ContentCountsState';
import { LensSubspaceName, SpaceInfo } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { ISanitizer } from '../../commonInfra/utils/Sanitize';
import { ILensDetailInfoWithUsage } from '../../ts/models/ILensInfo';
import { ILensActions } from '../../ts/ServerApi/types/ILensAction';
import { LensSubspaceProps } from '../types/LensSubspaceProps';
import { LensUsageAnalytics } from '../usageAnalytics/LensUsageAnalytics';
import { LensSpaceHeader } from './LensSpaceHeader';

const FFPrefix = ':ff_';

export interface LensSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly contentCounts: ISpaceInfoTypeItemCount<'lens'>;
  readonly contentCountsUpToDate: boolean;
  readonly siteUrlName: string;
}

export interface LensSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface LensSpaceOwnProps {
  readonly lensDetailInfoWithUsage: ILensDetailInfoWithUsage;
  readonly allowedActions: ILensActions;
  readonly subspaceProps: LensSubspaceProps;
  readonly canEdit: boolean;
  readonly reloadSpace: () => void;
  readonly sanitizer: ISanitizer;
}

export type LensSpaceProps = LensSpaceStateProps & LensSpaceOwnProps & LensSpaceDispatchProps;

function askDataSubspace(
  subspaceProps: LensSubspaceProps,
  siteUrlName: string,
): Subspace {
  return {
    name: 'askData',
    tabLabel: Messages.nlpTab(),
    renderContent: () => <iframe
      title='Ask data Lens interface'
      src={getNlpWorkbookUrl(
        siteUrlName,
        subspaceProps.lensDetailInfo.repositoryUrl)}
      className={iframeStyle}
    />,
    shouldRenderTabContentCount: false
  };
}


function getSubspaces(
  subspaceProps:  LensSubspaceProps,
  siteUrlName: string,
): ReadonlyArray<Subspace> {
  return [
    askDataSubspace(subspaceProps, siteUrlName),
  ];
}

export class LensSpace extends React.PureComponent<LensSpaceProps> {
  private memoizedGetSubspaces = defaultMemoize(getSubspaces);

  public urlForSubspace = (subspaceName?: LensSubspaceName): string => {
    switch (subspaceName) {
      case 'askData':
        return this.props.urls.lensUrl(this.props.lensDetailInfoWithUsage.id);
      case 'usageAnalytics':
        return this.props.urls.lensUsageAnalyticsUrl(this.props.lensDetailInfoWithUsage.id);
    }
  }

  public render() {
    const subspaces = this.memoizedGetSubspaces(
      this.props.subspaceProps,
      this.props.siteUrlName,
    );

    if (this.props.spaceInfo.subspaceName === 'usageAnalytics') {
      return <LensUsageAnalytics lensId={this.props.lensDetailInfoWithUsage.id}/>;
    }

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.urlForSubspace}
        mastheadElement={this.mastheadElement}
        testIdPrefix={'lens'}
        spaceItemName={this.props.lensDetailInfoWithUsage.name}
      />
    );
  }

  public componentDidMount() {
    this.handleTelemetry();
    this.props.fetchUpdatedContentCounts();
  }

  public componentDidUpdate(prevProps: LensSpaceProps) {
    this.props.fetchUpdatedContentCounts();
    if (this.props.lensDetailInfoWithUsage.id !== prevProps.lensDetailInfoWithUsage.id) {
      this.handleTelemetry();
    }
  }

  private handleTelemetry() {
    // TODO: Telemetry
  }

  private get mastheadElement(): React.ReactNode {
    return <LensSpaceHeader
      availableActions={this.props.allowedActions}
      item={this.props.lensDetailInfoWithUsage}
      urls={this.props.urls}
      canEdit={this.props.canEdit}
      sanitizer={this.props.sanitizer}
      reloadSpace={this.props.reloadSpace}
    />;
  }
}

export const iframeStyle = cssClass('lens-landing-page', 'iframe', {
  border: 'none',
  width: '100%',
  height: '100%',
});

const getNlpWorkbookUrl = (siteUrlName: string, lensRepositoryUrl: string): string => {
    const baseUrl = window.location.origin;
    const siteUrl = siteUrlName === '' ? '' : `/t/${siteUrlName}`;
    const parameters = getFeatureFlagParams(window.location);

  return `${baseUrl}${siteUrl}/startAskData/lens/${lensRepositoryUrl}${parameters}`;
};

const getFeatureFlagParams = (windowLocation: Location): string => {
  // need to filter feature flags from the parameters since the ask data page that embeded in vizportal has url like this:
  // {host}/startAskData/{dataSourceName}?:embed=y&:showVizHome=n&:apiID=host0#navType=1&navSrc=Parse
  const featureFlags = filterFeatureFlagsInUrl(windowLocation.href);
  return featureFlags.length > 0 ? '?' + featureFlags : '';
};

const filterFeatureFlagsInUrl = (url: string): string => {
  const params = QueryString.parseUrl(url).query;
  Object.keys(params).forEach((key: string) => params[key] = key.indexOf(FFPrefix) === 0 ? params[key] : undefined);
  return removeHash(QueryString.stringify(params, { encode: false }));
};

const removeHash = (urlWithHash: string): string => {
  return urlWithHash.split('#')[0];
};